import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, MessageSquare, Plus, Edit, Trash2, ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TemplatesSettings = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'email',
    category: 'follow_up',
    subject: '',
    body: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/templates`, formData);
      toast.success('Template created successfully');
      setCreateDialogOpen(false);
      setFormData({ name: '', type: 'email', category: 'follow_up', subject: '', body: '', is_active: true });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/templates/${selectedTemplate.id}`, formData);
      toast.success('Template updated successfully');
      setEditDialogOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      category: template.category,
      subject: template.subject || '',
      body: template.body,
      is_active: template.is_active
    });
    setEditDialogOpen(true);
  };

  const emailTemplates = templates.filter(t => t.type === 'email');
  const smsTemplates = templates.filter(t => t.type === 'sms');

  const getCategoryBadge = (category) => {
    const colors = {
      welcome: 'bg-blue-600',
      follow_up: 'bg-purple-600',
      reminder: 'bg-yellow-600',
      proposal: 'bg-green-600',
      appointment: 'bg-orange-600',
      estimate: 'bg-pink-600',
      thank_you: 'bg-teal-600',
      other: 'bg-gray-600'
    };
    return <Badge className={colors[category]}>{category.replace('_', ' ')}</Badge>;
  };

  const TemplateForm = ({ onSubmit, isEdit = false }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label>Template Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          placeholder="e.g., Welcome Email"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Type *</Label>
          <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Category *</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="welcome">Welcome</SelectItem>
              <SelectItem value="follow_up">Follow Up</SelectItem>
              <SelectItem value="reminder">Reminder</SelectItem>
              <SelectItem value="proposal">Proposal</SelectItem>
              <SelectItem value="appointment">Appointment</SelectItem>
              <SelectItem value="estimate">Estimate</SelectItem>
              <SelectItem value="thank_you">Thank You</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {formData.type === 'email' && (
        <div>
          <Label>Subject *</Label>
          <Input
            value={formData.subject}
            onChange={(e) => setFormData({...formData, subject: e.target.value})}
            placeholder="Email subject line"
            required={formData.type === 'email'}
          />
        </div>
      )}
      <div>
        <Label>Body *</Label>
        <Textarea
          value={formData.body}
          onChange={(e) => setFormData({...formData, body: e.target.value})}
          placeholder="Template content...\n\nUse variables like {first_name}, {last_name}, {phone}"
          rows={8}
          required
        />
      </div>
      <div className="bg-accent/50 p-3 rounded-lg">
        <p className="text-sm font-medium mb-1">Available Variables:</p>
        <p className="text-xs text-muted-foreground">
          {'{first_name}'}, {'{last_name}'}, {'{email}'}, {'{phone}'}, {'{company_name}'}, {'{address}'}
        </p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }}>Cancel</Button>
        <Button type="submit">{isEdit ? 'Update' : 'Create'} Template</Button>
      </DialogFooter>
    </form>
  );

  const TemplateCard = ({ template }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{template.name}</CardTitle>
            <div className="flex items-center space-x-2 mt-2">
              {getCategoryBadge(template.category)}
              {!template.is_active && <Badge variant="outline">Inactive</Badge>}
            </div>
          </div>
          {template.type === 'email' ? <Mail className="h-5 w-5 text-primary" /> : <MessageSquare className="h-5 w-5 text-primary" />}
        </div>
      </CardHeader>
      <CardContent>
        {template.type === 'email' && (
          <div className="mb-3">
            <p className="text-sm text-muted-foreground">Subject:</p>
            <p className="text-sm font-medium">{template.subject}</p>
          </div>
        )}
        <div className="mb-4">
          <p className="text-sm text-muted-foreground">Preview:</p>
          <p className="text-sm line-clamp-3">{template.body}</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(template)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDeleteTemplate(template.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Templates</h1>
              <p className="text-muted-foreground">Manage email and SMS templates</p>
            </div>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Template</DialogTitle>
                <DialogDescription>Create a reusable email or SMS template</DialogDescription>
              </DialogHeader>
              <TemplateForm onSubmit={handleCreateTemplate} />
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="email" className="space-y-4">
          <TabsList>
            <TabsTrigger value="email">
              <Mail className="h-4 w-4 mr-2" />
              Email Templates ({emailTemplates.length})
            </TabsTrigger>
            <TabsTrigger value="sms">
              <MessageSquare className="h-4 w-4 mr-2" />
              SMS Templates ({smsTemplates.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading templates...</p>
              </div>
            ) : emailTemplates.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No email templates yet</h3>
                  <p className="text-muted-foreground mb-4">Create your first email template</p>
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {emailTemplates.map(template => <TemplateCard key={template.id} template={template} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sms">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading templates...</p>
              </div>
            ) : smsTemplates.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No SMS templates yet</h3>
                  <p className="text-muted-foreground mb-4">Create your first SMS template</p>
                  <Button onClick={() => { setFormData({...formData, type: 'sms'}); setCreateDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {smsTemplates.map(template => <TemplateCard key={template.id} template={template} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Template</DialogTitle>
              <DialogDescription>Update template information</DialogDescription>
            </DialogHeader>
            <TemplateForm onSubmit={handleUpdateTemplate} isEdit />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};